import React, {useState} from 'react';
import {
    Box,
    Grid,
    Typography,
    Button,
    Paper,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    TextField,
    IconButton,
    TableHead, TableRow, TableCell, TableBody, Table,
} from '@mui/material';
import {Edit} from '@mui/icons-material';
import {withAuthenticationRequired} from '@auth0/auth0-react';
import Loading from '../components/common/Loading';
import {useTranslation} from 'react-i18next';
import {useMutation, useQuery} from '@apollo/client';
import {GET_CONFIGURATION_PARAMETERS, UPSERT_CONFIGURATION_PARAMETER} from "../gql/configuratieParameters.gql";
import AppSnackbar from "../common/AppSnackbar";
import Alert from '@mui/material/Alert';

const toInput = (values) => ({
    id: values?.id ? Number(values.id) : undefined,
    type: values?.type,
    startDate: values?.startDate || undefined,
    endDate: values?.endDate || undefined,
    amount: values?.amount ? String(values.amount).replace(',', '.') : undefined,
});

const ConfigurationParameterPage = () => {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [severity, setSeverity] = useState('success');

    const {data: dataAllParameters, loading, error} = useQuery(GET_CONFIGURATION_PARAMETERS);
    const [upsert] = useMutation(UPSERT_CONFIGURATION_PARAMETER);
    const BEHEER_PARAMETER_TYPES = ["BOOKING_OPEN_DATE", "SERVICE_FEE"];

    const [form, setForm] = useState({
        id: '',
        type: '',
        startDate: '',
        endDate: '',
        amount: '',
    });

    const handleOpen = (parameter, parameterType) => {
        if (parameter) {
            setForm({
                id: parameter.id,
                type: parameterType,
                startDate: parameter.startDate,
                endDate: parameter.endDate,
                amount: parameter.amount,
            });
        } else {
            setForm({
                id: "",
                type: parameterType,
                startDate: "",
                endDate: "",
                amount: ""
            });
        }
        setOpen(true);
    };
    const handleClose = () => setOpen(false);

    const onSubmit = async () => {
        try {
            upsert({
                variables: {input: toInput(form)},
                refetchQueries: [
                    {query: GET_CONFIGURATION_PARAMETERS}
                ],
            }).then(() => {
                setSnackbarMessage(t('configuration.save.success'));
                setSeverity('success');
                setOpenSnackbar(true);
            }).catch(() => {
                setSnackbarMessage(t('configuration.save.error'));
                setSeverity('error');
                setOpenSnackbar(true);
            }).finally(() => handleClose());
        } catch (e) {
            setSnackbarMessage(t('errors.saveFailed'));
            setSeverity('error');
            setOpenSnackbar(true);
        }
    };

    const onChange = (e) => {
        const {name, value} = e.target;
        setForm((prev) => ({...prev, [name]: value}));
    };

    return (
        <Box sx={{width: '100%', display: 'block', textAlign: 'left'}}>
            {/* Header */}
            <Grid container alignItems="center" justifyContent="space-between" sx={{mb: 3, width: '100%'}} wrap="nowrap">
                <Grid item>
                    <Typography variant="h4" component="h2">
                        {t('configuration.pageTitle', 'Configuratie parameter')}
                    </Typography>
                </Grid>
            </Grid>

            {/* Paper */}
            <Paper variant="outlined" sx={{p: 3}}>
                {loading ? (
                    <Box sx={{height: 200, display: 'grid', placeItems: 'center'}}>
                        <CircularProgress/>
                    </Box>
                ) : error ? (
                    <Alert severity="error">
                        {t('errors.loadFailed', 'Laden mislukt')}: {String(error.message)}
                    </Alert>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('type')}</TableCell>
                                <TableCell>{t('startDate')}</TableCell>
                                <TableCell>{t('endDate')}</TableCell>
                                <TableCell>{t('amount')}</TableCell>
                                <TableCell>{t('actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {BEHEER_PARAMETER_TYPES.map((parameter) => {
                                const hit = dataAllParameters?.configuratieParameters?.find(
                                    (p) => p.type === parameter
                                );

                                return (
                                    <TableRow key={parameter.type} hover>
                                        <TableCell>{t(parameter)}</TableCell>
                                        <TableCell>{hit ? hit.startDate : ""}</TableCell>
                                        <TableCell>{hit ? hit.endDate : ""}</TableCell>
                                        <TableCell>
                                            {parameter === "BOOKING_OPEN_DATE"
                                                ? "/"
                                                : hit
                                                    ? hit.amount
                                                    : ""}
                                        </TableCell>
                                        <TableCell align="center" sx={{whiteSpace: "nowrap"}}>
                                            <IconButton
                                                onClick={() => handleOpen(hit, parameter)}
                                                color="primary"
                                                data-testid={`edit-config-param-${parameter}`}
                                            >
                                                <Edit/>
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </Paper>

            {/* Dialog */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>{t('editConfiguratieParameter')}</DialogTitle>
                <DialogContent>
                    <TextField
                        margin="normal"
                        type={"date"}
                        label={t('startDate')}
                        name="startDate"
                        value={form.startDate}
                        onChange={onChange}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        required
                        inputProps={{maxLength: 50}}
                    />
                    <TextField
                        margin="normal"
                        type={"date"}
                        label={t('endDate')}
                        name="endDate"
                        value={form.endDate}
                        InputLabelProps={{ shrink: true }}
                        onChange={onChange}
                        fullWidth
                        required
                        inputProps={{maxLength: 50}}
                    />
                    {form.type !== 'BOOKING_OPEN_DATE' ? <TextField
                        margin="normal"
                        type={"number"}
                        label={t('amount')}
                        name="amount"
                        value={form.amount}
                        onChange={onChange}
                        fullWidth
                        inputProps={{maxLength: 300}}
                    /> : null}
                    <TextField
                        select
                        margin="normal"
                        label={t('type')}
                        name="type"
                        disabled
                        value={form.type}
                        onChange={onChange}
                        fullWidth
                        required
                    >
                        {BEHEER_PARAMETER_TYPES.map((type) => (
                            <MenuItem key={type} value={type}>
                                {t(type)}
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} data-testid="cancel-config-param-button">{t('cancel')}</Button>
                    <Button variant="contained" onClick={onSubmit} data-testid="save-config-param-button">
                        {t('save')}
                    </Button>
                </DialogActions>
            </Dialog>


            <AppSnackbar openSnackbar={openSnackbar}
                         snackbarMessage={snackbarMessage}
                         setOpenSnackbar={setOpenSnackbar}
                         severity={severity}/>
        </Box>
    );
};

export default withAuthenticationRequired(ConfigurationParameterPage, {
    onRedirecting: () => <Loading/>,
    returnTo: "/beheer/login"
});
